import {
  AbstractMesh,
  BlurPostProcess,
  BoundingBox,
  CascadedShadowGenerator,
  Color3,
  ConvolutionPostProcess,
  DirectionalLight,
  Frustum,
  FxaaPostProcess,
  GizmoManager,
  GPUParticleSystem,
  GroundMesh,
  HemisphericLight,
  HighlightLayer,
  HighlightsPostProcess,
  ImageAssetTask,
  LensFlare,
  LensFlareSystem,
  Material,
  Mesh,
  MeshBuilder,
  MotionBlurPostProcess,
  MultiMaterial,
  ParticleSystem,
  Path3D,
  PBRMaterial,
  PhotoDome,
  PhysicsBody,
  PhysicsMotionType,
  PhysicsShapeMesh,
  PhysicsShapeType,
  Quaternion,
  Ray,
  Scene,
  Sprite,
  SpriteManager,
  SpritePackedManager,
  StandardMaterial,
  Texture,
  Vector2,
  Vector3,
  VertexBuffer,
  VertexData,
} from '@babylonjs/core';
import { WaterMaterial } from '@babylonjs/materials';
import { NumberGenerator } from 'number-generator';
import aleaRNGFactory from 'number-generator/lib/aleaRNGFactory';
import { Asset, AssetType } from './Asset';
import { AssetBasedConfig, AssetObject } from './AssetObject';
import { CameraController } from './CameraController';
import { CheckpointGenerator } from './CheckpointGenerator';
import { CollectibleGenerator } from './CollectibleGenerator';
import { DecorationGenerator } from './DecorationGenerator';
import { DungeonGenerator, DungeonRoom } from './DungeonGenerator';
import { UnderwaterEffect } from './effects/UnderwaterEffect';
import { EnemyGenerator } from './EnemyGenerator';
import { Game } from './Game';
import { GameConfig } from './GameConfig';
import { GameState } from './GameState';
import { GrassGenerator } from './GrassGenerator';
import { Gui } from './gui/Gui';
import { Hud } from './Hud';
import { InputManager } from './InputManager';
import { LevelConfig, WayGenerationConfig } from './LevelConfig';
import { MeshCutter } from './MeshCutter';
import { AssetObjectConfig, ExistingMeshConfig, GroundConfig, MeshFactory } from './MeshFactory';
import { Noise } from './Noise';
import { ObstacleGenerator } from './ObstacleGenerator';
import { Particles } from './Particles';
import { PathFactory } from './PathFactory';
import { PathLightGenerator } from './PathLightGenerator';
import { Platform } from './PlatformFactory';
import { PlatformGenerator } from './PlatformGenerator';
import { Player } from './Player';
import { PositionFactory } from './PositionFactory';
import { RainBuilder } from './RainBuilder';
import { DecorationPlacementTool } from './sceneviewer/DecorationPlacementTool';
import { LevelConfigTool } from './sceneviewer/LevelConfigTool';
import { PathEditorTool } from './sceneviewer/PathEditorTool';
import { TextureAtlas } from './TextureAtlas';

export interface EntityGenerator<T extends AssetBasedConfig<any>> {
  generate(config: T): Promise<void>;
  update(deltaTime: number): void;
  dispose(): void;
}

export interface PathInfo {
  pathIndex: number;
  pathPoint: Vector3;
  nextPoint: Vector3;
  prevPoint: Vector3;
  tangent: Vector3;
  lateral: Vector3;
  yRotation: number;
}

export interface LevelResult {
  key: string;
  name: string;
  remainingTime: number;
  score: number;
  collectibles: number;
  enemies: number;
  totalCollectibles: number;
  totalEnemies: number;
  coins: number;
  lives: number;
  attempts: number;
}

export interface WayMesh {
  mesh: Mesh;
  pathIndexes: number[];
}

export class Level {
  static fullGravity: Vector3 = new Vector3(0, -9.81, 0);
  static halfGravity: Vector3 = new Vector3(0, -5, 0);
  static zeroGravity: Vector3 = new Vector3(0, 0, 0);
  static usePbrMaterial: boolean = true;

  platforms: Platform[] = [];
  wayMeshes: WayMesh[] = [];
  skybox: Mesh;
  ground: GroundMesh;
  ceiling: GroundMesh;
  water: GroundMesh;
  baseSceneMeshes: Mesh[];
  collisionMeshes: Mesh[] = [];
  materials: Material[] = [];

  /**
   * Path boundary walls that follow the player and prevent leaving the path
   */
  pathBoundaryWalls: { left: Mesh; right: Mesh } | null = null;

  collectibles: number = 0;
  enemies: number = 0;

  light: DirectionalLight;
  hemisphericLight: HemisphericLight;

  /**
   * The shadow generator of the level
   */
  shadowGenerator: CascadedShadowGenerator;

  /**
   * All registered shadow casters for distance-based filtering
   */
  private allShadowCasters: AbstractMesh[] = [];

  /**
   * The decoration generator of the level
   */
  decorationGenerator: DecorationGenerator;

  /**
   * The platform generator of the level
   */
  platformGenerator: PlatformGenerator;

  /**
   * The grass generator of the level
   */
  grassGenerator: GrassGenerator;

  /**
   * The collectible generator of the level
   */
  collectibleGenerator: CollectibleGenerator;

  /**
   * The dungeon generator of the level
   */
  dungeonGenerator: DungeonGenerator;

  /**
   * The enemy generator of the level
   */
  enemyGenerator: EnemyGenerator;

  /**
   * The obstacle generator of the level
   */
  obstacleGenerator: ObstacleGenerator;

  /**
   * The checkpoint generator of the level
   */
  checkpointGenerator: CheckpointGenerator;

  /**
   * The path light generator of the level
   */
  pathLightGenerator: PathLightGenerator;

  /**
   * The highlight layer of the level
   */
  highlights: HighlightLayer;

  /**
   * The particles of the level
   */
  particles: Particles;

  rng: NumberGenerator;
  noise: Noise;
  positionFactory: PositionFactory;
  pathFactory: PathFactory;

  /**
   * The scene of the level, a reference to the game scene
   */
  scene: Scene;

  /**
   * The player of the level
   */
  player!: Player;

  /**
   * The camera controller of the level
   */
  cameraController!: CameraController;

  /**
   * The input manager of the level
   */
  inputManager!: InputManager;

  /**
   * The time limit of the level
   */
  timeLimit: number;

  /**
   * The handle of the time limit
   */
  timeLimitHandle: any;

  startTime: number;

  /**
   * The HUD of the level
   */
  hud: Hud;

  /**
   * The path of the level
   */
  pathPoints: Vector3[];

  /**
   * The indexes of the path that are occupied by checkpoints, obstacles or enemies
   */
  occupiedPathIndexes: number[] = [];

  /**
   * Time scale for slow or fast motion effects an player handling
   */
  timeScale: number = 1;

  /**
   * Global sssets used in every level
   */
  assets: Asset[] = [
    new Asset('player', AssetType.MESH, 'assets/player/', 'player.glb'),
    new Asset('prize-wheel', AssetType.MESH, 'assets/model/arcade/', 'prize-wheel.glb'),
    new Asset('crate', AssetType.MESH, 'assets/model/platformer/', 'crate-item-strong.glb'),
    new Asset('coin', AssetType.MESH, 'assets/model/platformer/', 'coin-gold.glb'),
    new Asset('bomb', AssetType.MESH, 'assets/model/platformer/', 'bomb.glb'),
    new Asset('obstacleProjectile', AssetType.MESH, 'assets/model/platformer_shiny/neutral/', 'bomb.gltf'),
    new Asset('projectile', AssetType.MESH, 'assets/model/food/', 'apple.glb'),
    new Asset('jewel', AssetType.MESH, 'assets/model/platformer/', 'jewel.glb'),
    new Asset('heart', AssetType.MESH, 'assets/model/platformer/', 'heart.glb'),
    new Asset('cannon-ball', AssetType.MESH, 'assets/model/pirate/', 'cannon-ball.glb'),
    new Asset('goal', AssetType.MESH, 'assets/model/space/', 'gate_complex.glb'),
    new Asset('checkpoint', AssetType.MESH, 'assets/model/prototype/', 'flag.glb'),
    new Asset('shield', AssetType.MESH, 'assets/model/prototype/', 'weapon-shield.glb'),
  ];

  /**
   * The global asset objects for every level
   */
  assetObjects: AssetObject[] = [
    new AssetObject('goal').withType('goal').withScale(5).withCenterOrigin(true).withComputeCenter(true),
    new AssetObject('checkpoint').withType('checkpoint').withClone(true),
  ];

  /**
   * Whether the level has been initialized
   */
  initialized = false;

  /**
   * Whether the level has been finished
   */
  finished = false;

  /**
   * The goal mesh for distance checking
   */
  private goalMesh: AbstractMesh;

  /**
   * Whether the goal portal effect has been activated
   */
  private goalActivated = false;

  /**
   * The time when the goal was activated (for 2 second countdown)
   */
  private goalActivationTime: number = 0;

  /**
   * The particle system for the goal portal effect
   */
  private goalPortalParticles: ParticleSystem | GPUParticleSystem;

  /**
   * Last time goal proximity was checked (for performance throttling)
   */
  private lastGoalProximityCheck: number = 0;

  /**
   * Whether the hurry up message has been shown
   */
  private hurryUpShown: boolean = false;

  constructor(
    public game: Game,
    public key: string,
    public config: LevelConfig
  ) {
    this.scene = this.game.scene;

    if (GameConfig.logInfo) {
      console.info('[Level] Config:');
      console.info(this.config.toJson());
    }

    this.highlights = new HighlightLayer('highlightLayer', this.scene);
    this.highlights.innerGlow = false; // Disable inner glow to prevent z-fighting flickering on distant meshes
    this.rng = aleaRNGFactory(this.config.seed);
    this.noise = new Noise(this.config.seed);
    this.particles = new Particles(this);
    this.positionFactory = new PositionFactory(this);
    this.pathFactory = new PathFactory(this);
    this.decorationGenerator = new DecorationGenerator(this);
    this.platformGenerator = new PlatformGenerator(this);
    this.grassGenerator = new GrassGenerator(this);
    this.collectibleGenerator = new CollectibleGenerator(this);
    this.dungeonGenerator = new DungeonGenerator(this, this.config.dungeon);
    this.enemyGenerator = new EnemyGenerator(this);
    this.obstacleGenerator = new ObstacleGenerator(this);
    this.checkpointGenerator = new CheckpointGenerator(this);
    this.pathLightGenerator = new PathLightGenerator(this);

    this.timeLimit = this.config.timeLimit;

    this.inputManager = new InputManager(this);

    this.createPath();

    this.game.physicsPlugin.setGravity(this.config.gravity);

    this.cameraController = new CameraController(this);

    if (this.config.sound?.backgroundMusic) {
      this.game.sound.createBackgroundMusic(this.config.sound.backgroundMusic);
    }
  }

  createPath() {
    if (this.config.dungeon) {
      this.pathPoints = this.dungeonGenerator.generatePath();
    } else if (this.config.path.predefined) {
      this.pathPoints = [];
      for (const point of this.config.path.predefined) {
        this.pathPoints.push(point.clone());
      }
    } else {
      this.pathPoints = this.pathFactory.createLevelPath(this.config.path);
    }

    if (this.game.config.debug && this.game.config.debugPaths) {
      this.game.debugPath3D(new Path3D(this.pathPoints));
    }
  }

  async createMaterials() {
    this.game.loadingScreen.setTask('Creating Materials');
    if (this.config.material.colorMaterialCount > 0) {
      const materials = this.game.materialService.createRandomColorMaterials(
        this.config.material.colorMaterialBaseColor,
        this.config.material.colorMaterialCount,
        () => {
          return this.rng.uFloat32();
        }
      );
      this.materials.push(...materials);
    }
    if (this.config.material.textures) {
      for (const textureConfig of this.config.material.textures) {
        if (!textureConfig.url && textureConfig.color) {
          if (Level.usePbrMaterial) {
            const mat = this.game.materialService.createPBRColorMaterial(
              textureConfig.name || 'color-' + textureConfig.color,
              Color3.FromHexString(textureConfig.color),
              textureConfig.alpha
            );

            if (textureConfig.roughness) {
              mat.roughness = textureConfig.roughness;
            }
            if (textureConfig.metallic) {
              mat.metallic = textureConfig.metallic;
            }
            this.materials.push(mat);
          } else {
            const mat = this.game.materialService.createColorMaterial(
              textureConfig.name || 'color-' + textureConfig.color,
              Color3.FromHexString(textureConfig.color),
              textureConfig.alpha | 1
            );
            this.materials.push(mat);
          }
        } else if (textureConfig.shader) {
          const shader = await this.game.shaders.loadFragmentShader(textureConfig.shader);
          const mat = this.game.shaders.createShaderMaterial(textureConfig.name || 'shader-' + textureConfig.shader, shader);
          if (textureConfig.alpha) {
            mat.alpha = textureConfig.alpha;
          }
          this.materials.push(mat);
        } else if (textureConfig.url) {
          let name = textureConfig.name;

          if (!name) {
            // file url to name
            name = textureConfig.url;
            name = name.substring(name.lastIndexOf('/') + 1);
            name = name.substring(0, name.lastIndexOf('.'));
          }

          if (Level.usePbrMaterial) {
            const mat = this.game.materialService.createPBRMaterial(
              name,
              textureConfig.url,
              textureConfig.uScale,
              textureConfig.vScale,
              textureConfig.uOffset,
              textureConfig.vOffset,
              textureConfig.bumpUrl
            );
            if (textureConfig.alpha) {
              mat.alpha = textureConfig.alpha;
            }
            if (textureConfig.roughness) {
              mat.roughness = textureConfig.roughness;
            }
            if (textureConfig.metallic) {
              mat.metallic = textureConfig.metallic;
            }
            this.materials.push(mat);
          } else {
            const mat = this.game.materialService.createMaterial(
              name,
              textureConfig.url,
              textureConfig.uScale,
              textureConfig.vScale,
              textureConfig.uOffset,
              textureConfig.vOffset
            );
            if (textureConfig.bumpUrl) {
              mat.bumpTexture = this.game.materialService.createTexture(textureConfig.bumpUrl, textureConfig.uScale, textureConfig.vScale);
            }
            if (textureConfig.alpha) {
              mat.alpha = textureConfig.alpha;
            }
            this.materials.push(mat);
          }
        }
      }
    }
    if (this.config.material.globalTextureNames) {
      for (const name of this.config.material.globalTextureNames) {
        this.materials.push(this.game.materialService.getMaterial(name));
      }
    }

    // Increase max lights for all materials to support path lights
    for (const material of this.materials) {
      if ('maxSimultaneousLights' in material) {
        (material as any).maxSimultaneousLights = 16;
      }
    }

    if (GameConfig.logInfo) {
      console.info('[Level] Materials:');
      for (let i = 0; i < this.materials.length; i++) {
        console.info(i, this.materials[i].name);
      }
    }
  }

  createLight() {
    this.game.loadingScreen.setTask('Creating Light');
    // hemispheric
    this.hemisphericLight = new HemisphericLight('hemisphericLight', this.config.light.hemiDirection, this.scene);
    this.hemisphericLight.intensity = this.config.light.hemiIntensity;
    this.hemisphericLight.groundColor = this.config.light.hemiGroundColor;

    // directional
    this.light = new DirectionalLight('directionalLight', this.config.light.direction, this.scene);
    this.light.intensity = this.config.light.intensity;
    this.light.position = this.config.light.position;
    this.light.diffuse = this.config.light.diffuse;

    if (this.game.config.sceneViewer) {
      // Path Editor Tool for creating predefined paths
      const pathEditor = new PathEditorTool(this);
      pathEditor.initialize();

      // Decoration Placement Tool for absolute placements
      const decorationPlacer = new DecorationPlacementTool(this);
      decorationPlacer.initialize();

      // Tools around level config
      const levelConfigTool = new LevelConfigTool(this);
      levelConfigTool.initialize();

      const lightSphere = MeshBuilder.CreateSphere('lightSphere', { diameter: 2 }, this.scene);
      lightSphere.position = this.light.position.clone();

      // Create a material for the sphere to make it visible
      const material = new StandardMaterial('lightSphereMat', this.scene);
      material.emissiveColor = new Color3(1, 1, 0); // Yellow
      lightSphere.material = material;

      // Direct the sphere to look at the light direction
      const target = lightSphere.position.add(this.config.light.direction);
      lightSphere.lookAt(target);

      // Parent the light to the sphere
      this.light.parent = lightSphere;

      // Reset light local transformation so it follows the parent
      this.light.direction = new Vector3(0, 0, 1);
      this.light.position = Vector3.Zero();

      // Add Gizmo
      const gizmoManager = new GizmoManager(this.scene);
      gizmoManager.positionGizmoEnabled = true;
      gizmoManager.rotationGizmoEnabled = true;
      gizmoManager.attachToMesh(lightSphere);
    }

    // Cascaded Shadow Maps for high-quality shadows across the scene
    this.shadowGenerator = new CascadedShadowGenerator(this.config.light.mapSize, this.light);
    this.shadowGenerator.darkness = this.config.light.darkness;
    this.shadowGenerator.bias = this.config.light.bias;
    this.shadowGenerator.normalBias = this.config.light.normalBias;
    this.shadowGenerator.stabilizeCascades = this.config.light.stabilizeCascades;
    this.shadowGenerator.lambda = this.config.light.lambda;
    this.shadowGenerator.cascadeBlendPercentage = this.config.light.cascadeBlendPercentage;
    this.shadowGenerator.depthClamp = this.config.light.depthClamp;

    // For orthographic cameras, adjust shadow settings to prevent disappearance
    if (this.config.camera.orthographic) {
      // autoCalcDepthBounds once only
      this.shadowGenerator.autoCalcDepthBounds = true;
      this.shadowGenerator.autoCalcDepthBoundsRefreshRate = 0;
    } else {
      this.shadowGenerator.autoCalcDepthBounds = this.config.light.autoCalcDepthBounds;
    }

    /* this.particles
      .createSunParticleSystem(this.light.position)
      .then((sun: ParticleSystemSet) => {
        sun.start();
      }); */

    // lensflare
    if (this.config.light.lensflare) {
      const lensFlareSystem = new LensFlareSystem('lensFlareSystem', this.light, this.scene);
      const lensFlareColor = new Color3(0.6, 0.6, 0.6);
      const lensFlareColor2 = new Color3(0.5, 0.5, 1);
      const lensFlareColor3 = new Color3(0.6, 0.5, 0.6);
      const lensFlareSizeFactor = 0.4;
      new LensFlare(0.2 * lensFlareSizeFactor, 0, lensFlareColor, 'assets/lensflare/lens5.png', lensFlareSystem);
      new LensFlare(0.3 * lensFlareSizeFactor, 0.2, lensFlareColor2, 'assets/lensflare/lens4.png', lensFlareSystem);
      new LensFlare(0.2 * lensFlareSizeFactor, 1.0, lensFlareColor, 'assets/lensflare/lens4.png', lensFlareSystem);
      new LensFlare(0.4 * lensFlareSizeFactor, 0.4, lensFlareColor3, 'assets/lensflare/flare.png', lensFlareSystem);
      new LensFlare(0.1 * lensFlareSizeFactor, 0.6, lensFlareColor, 'assets/lensflare/lens5.png', lensFlareSystem);
      new LensFlare(0.2 * lensFlareSizeFactor, 0.8, lensFlareColor, 'assets/lensflare/lens4.png', lensFlareSystem);
    }
  }

  createFog() {
    if (this.config.fog) {
      this.scene.fogMode = this.config.fog.mode;
      this.scene.fogDensity = this.config.fog.density;
      this.scene.fogColor = this.config.fog.color;
      this.scene.fogStart = this.config.fog.fogStart;
      this.scene.fogEnd = this.config.fog.fogEnd;
    }
  }

  async createSkyBox() {
    if (!this.config.skybox) {
      return;
    }

    this.game.loadingScreen.setTask('Creating Skybox');

    if (this.config.skybox.clearColor) {
      this.scene.clearColor = this.config.skybox.clearColor;
      return;
    }

    if (this.config.skybox.photoDome) {
      const size = this.config.skybox.scale || 10000;
      const dome = new PhotoDome(
        'skybox_dome',
        this.config.skybox.url,
        {
          resolution: 32,
          size: size,
          loop: true,
        },
        this.scene
      );
      if (this.config.skybox.rotation) {
        dome.rotation = this.config.skybox.rotation;
      }

      this.scene.environmentTexture = dome.material.diffuseTexture;
      dome.material.fogEnabled = false;

      return dome;
    }

    if (this.config.skybox.url) {
      this.skybox = await MeshFactory.createSkyBox(this.config.skybox, 'skyBox', this.scene);
    }
  }

  async createWater(): Promise<GroundMesh> {
    if (this.config.water === undefined) {
      return;
    }

    this.game.loadingScreen.setTask('Creating Water');

    if (this.config.water.effect && !this.game.config.sceneViewer) {
      const underwaterEffect = new UnderwaterEffect(this);
    }

    if (this.config.water.mesh) {
      const waterMaterial = new WaterMaterial('water', this.game.scene, this.config.water.renderTargetSize);
      waterMaterial.backFaceCulling = this.config.water.backFaceCulling ?? true;
      waterMaterial.bumpTexture = new Texture(this.config.water.bumpTextureUrl, this.game.scene);
      waterMaterial.windForce = this.config.water.windForce;
      waterMaterial.waveHeight = this.config.water.waveHeight;
      waterMaterial.bumpHeight = this.config.water.bumpHeight;
      waterMaterial.windDirection = this.config.water.windDirection;
      waterMaterial.waterColor = this.config.water.waterColor;
      waterMaterial.colorBlendFactor = this.config.water.colorBlendFactor;
      waterMaterial.alpha = this.config.water.alpha;
      waterMaterial.waveLength = this.config.water.waveLength;

      if (this.skybox) {
        waterMaterial.addToRenderList(this.skybox);
      }

      if (this.ground) {
        waterMaterial.addToRenderList(this.ground);
      }

      if (this.ceiling) {
        waterMaterial.addToRenderList(this.ceiling);
      }

      if (this.wayMeshes) {
        for (const mesh of this.wayMeshes) {
          waterMaterial.addToRenderList(mesh);
        }
      }

      (waterMaterial.bumpTexture as Texture).uScale = this.config.water.bumpScale;
      (waterMaterial.bumpTexture as Texture).vScale = this.config.water.bumpScale;

      const centerPathPoint = this.getCenterPathPoint();
      const waterConfig = GroundConfig.builder()
        .name('water')
        .size(new Vector3(this.config.water.size, this.config.water.size, this.config.water.depth))
        .material(waterMaterial)
        .physicsShapeType(undefined)
        .position(new Vector3(centerPathPoint.x, this.config.water.level, centerPathPoint.y))
        .build();

      const mesh = waterConfig.buildMesh(this);
      MeshFactory.configureMesh(mesh, waterConfig, this);

      return mesh as GroundMesh;
    }

    return undefined;
  }

  async createBaseSceneMeshes(): Promise<Mesh[]> {
    this.game.loadingScreen.setTask('Creating base scene meshes');

    const obj = this.config.baseScene.object;
    const scale = obj.scale ?? 1;
    const asset = this.game.assetFactory.getAsset(obj.asset);

    // getMeshes() correctly unparents children from the disabled root container
    const rawMeshes = asset.getMeshes();
    const subMeshes = Asset.filterMeshes(rawMeshes);

    // Also enable any TransformNodes so no ancestor hides the geometry
    const task = asset.getTask<any>();
    if (task?.loadedTransformNodes) {
      for (const tn of task.loadedTransformNodes) {
        tn.setEnabled(true);
      }
    }

    if (!subMeshes || subMeshes.length === 0) {
      console.warn('[Level] createBaseSceneMeshes: no meshes found in asset', obj.asset);
      return;
    }

    if (GameConfig.logDebug) {
      console.log('[Level] createBaseSceneMeshes: found', subMeshes.length, 'sub-meshes');
    }

    let meshes: Mesh[] = [];

    for (const sm of subMeshes) {
      if (this.config.baseScene.exclude.includes(sm.name)) {
        sm.setParent(null); // detach from any parent
        sm.isVisible = false;
        sm.setEnabled(false);
        sm.dispose();
        continue;
      }

      sm.setParent(null); // detach from any parent
      sm.isVisible = true;
      sm.setEnabled(true);
      sm.scaling.scaleInPlace(scale);
      sm.receiveShadows = true;
      sm.isPickable = true;

      if (!this.config.baseScene.disableGround.includes(sm.name)) {
        sm.metadata = Object.assign(sm.metadata || {}, {
          ground: true,
          projectileCollision: true,
        });
      }

      if (sm.material instanceof PBRMaterial) {
        const mat = sm.material;
        mat.roughness = 1;
        mat.metallic = 0;

        // Force OPAQUE mode — prevents BabylonJS from putting this mesh in the alpha-blended render queue
        // where it gets camera-distance sorted and renders in front of closer opaque geometry.
        if (mat.alpha >= 1 && !mat.useAlphaFromAlbedoTexture && !mat.opacityTexture) {
          mat.transparencyMode = PBRMaterial.PBRMATERIAL_OPAQUE;
          mat.forceAlphaTest = false;
        } else {
          // Truly transparent: depth pre-pass prevents self-occlusion artefacts
          mat.needDepthPrePass = true;
          mat.separateCullingPass = true;
        }
        // prevents faces from vanishing when camera looks at them from slightly behind
        mat.backFaceCulling = false;
        // disable environment reflection to keep colours stable
        mat.environmentIntensity = 0;
      }

      // Fix 4: Refresh bounding info so Babylon's frustum culling doesn't
      // incorrectly cull meshes that are still in view after re-parenting.
      sm.refreshBoundingInfo();

      sm.position.addInPlace(this.config.baseScene.offset);

      const disablePhysics = this.config.baseScene.disablePhysics.includes(sm.name);

      if (!disablePhysics) {
        // Give each sub-mesh its own static physics body
        try {
          const shape = new PhysicsShapeMesh(sm, this.scene);
          shape.material = { friction: 1, restitution: 0 };
          const body = new PhysicsBody(sm, PhysicsMotionType.STATIC, true, this.scene);
          body.shape = shape;
          body.setMassProperties({ mass: 0 });
        } catch (e) {
          console.warn('[Level] createBaseSceneMeshes: physics failed for sub-mesh', sm.name, e);
        }
      }

      meshes.push(sm);
    }

    return meshes;
  }

  async createPathLights() {
    this.game.loadingScreen.setTask('Creating Path Lights');
    this.pathLightGenerator.generate();
  }

  async createWay(): Promise<void> {
    this.game.loadingScreen.setTask('Creating Way');
    const config = this.config.way;
    const segments: { points: Vector3[]; indexes: number[] }[] = [];
    let currentSegment: { points: Vector3[]; indexes: number[] } = { points: [], indexes: [] };

    // Always add start point
    currentSegment.points.push(this.pathPoints[0]);

    let i = 1;
    while (i < this.pathPoints.length) {
      const isGap = this.floatInRange(0, 1) < (config.gapProbability || 0);

      // Determine length of this segment (gap or way)
      // If it's a gap, usage gapSize config, otherwise random length (e.g. 5-15 points)
      let segmentLength = 1;
      if (isGap && config.gapSize) {
        segmentLength = this.integerInRange(config.gapSize.min, config.gapSize.max);
      } else {
        // Way segments should be at least a few points long to look good
        segmentLength = this.integerInRange(5, 15);
      }

      // Ensure we don't go out of bounds
      const endIndex = Math.min(i + segmentLength, this.pathPoints.length);

      if (isGap) {
        // If we have a current segment accumulating, push it to segments
        if (currentSegment.points.length > 1) {
          segments.push(currentSegment);
        }
        // Start a new path segment after the gap
        currentSegment = { points: [], indexes: [] };
        // Add the point AFTER the gap as the start of new segment (if available)
        if (endIndex < this.pathPoints.length) {
          currentSegment.indexes.push(endIndex);
          currentSegment.points.push(this.pathPoints[endIndex]);
        }
        for (let j = i - 1; j <= endIndex + 1; j++) {
          this.occupiedPathIndexes.push(j); // see gaps as occupied path points
        }
      } else {
        // Add points to current segment
        for (let j = i; j < endIndex; j++) {
          currentSegment.indexes.push(j);
          currentSegment.points.push(this.pathPoints[j]);
        }
      }

      i = endIndex;
    }

    // Push remaining segment
    if (currentSegment.points.length > 1) {
      segments.push(currentSegment);
    }

    // If no probability set, just use full path (backward compatibility/safety)
    if ((config.gapProbability || 0) <= 0) {
      segments.length = 0;
      const indexes = [];
      for (let i = 0; i < this.pathPoints.length; i++) {
        indexes.push(i);
      }
      segments.push({ points: this.pathPoints, indexes: indexes });
    }

    // Create meshes for each segment
    for (const segment of segments) {
      const segmentPoints = segment.points;
      if (segmentPoints.length < 2) continue;

      let path: Mesh;
      if (config.shape) {
        path = this.pathFactory.createPathMeshWithShape('way', segmentPoints, config.shape, config.yOffset);
      } else if (config.height) {
        path = this.pathFactory.createPathMeshWithHeight('way', segmentPoints, config.width, config.height, config.yOffset);
      } else if (config.width) {
        path = this.pathFactory.createPathMesh('way', segmentPoints, config.width, config.segmentsAcross, config.yOffset);
      } else {
        continue;
      }

      if (config.sideMaterial) {
        const material = new MultiMaterial('wayMaterial', this.scene);
        const topMat = this.getMaterial(config.material);
        const sideMat = this.getMaterial(config.sideMaterial);
        topMat.backFaceCulling = false;
        sideMat.backFaceCulling = false;
        material.subMaterials.push(topMat);
        material.subMaterials.push(sideMat);
        path.material = material;
      } else {
        path.material = this.getMaterial(config.material);
        path.material.backFaceCulling = false;
      }
      path.checkCollisions = true;
      path.isPickable = false;
      path.metadata = {
        projectileCollision: true,
        way: true,
      };
      const meshConfig = ExistingMeshConfig.builder()
        .name('way')
        .physicsShapeType(PhysicsShapeType.MESH)
        .receiveShadows(true)
        .mesh(path)
        .build();
      MeshFactory.configureMesh(path, meshConfig, this);

      this.wayMeshes.push({ mesh: path, pathIndexes: segment.indexes });
    }

    // cut holes into way meshes if configured
    if ((config.holeProbability || 0) > 0 && config.holeSize) {
      await this.cutHolesInWay(config);
    }
  }

  private async cutHolesInWay(config: WayGenerationConfig): Promise<void> {
    this.game.loadingScreen.setTask('Cutting Holes');
    MeshCutter.cutHolesInWayCSGLegacy(this, config, this.wayMeshes);

    // Initialize CSG2/Manifold library for boolean operations
    //await InitializeCSG2Async();
    // if (config.shape) {
    //   await MeshCutter.cutHolesInWayCSGLegacy(this, config, this.wayMeshes);
    // } else {
    //   await MeshCutter.cutHolesInWayRemovingTriangles(this, config, this.wayMeshes);
    // }
  }

  async createDecoration(): Promise<void> {
    const config = this.config.decoration;
    if (!config || !config.distributions || config.distributions.length === 0) {
      return;
    }

    this.game.loadingScreen.setTask('Creating Decoration');
    await this.decorationGenerator.generate(config);
  }

  async createGrass(): Promise<void> {
    const config = this.config.grass;
    if (!config) {
      return;
    }

    this.game.loadingScreen.setTask('Planting Grass');
    await this.grassGenerator.generate(config);
  }

  async createPlatforms() {
    const config = this.config.platform;
    if (!config) {
      return;
    }

    this.game.loadingScreen.setTask('Creating Platforms');
    await this.platformGenerator.generate(config);
  }

  async createCeiling() {
    const config = this.config.ceiling;
    if (!config) {
      return;
    }
    this.game.loadingScreen.setTask('Creating Ceiling');
    this.ceiling = (await this.config.ceiling.buildMeshAsync(this)) as GroundMesh;

    if (this.config.ceiling.metadata?.centerOnPath) {
      const centerPathPoint = this.getCenterPathPoint();
      this.ceiling.position.x = centerPathPoint.x;
      this.ceiling.position.z = centerPathPoint.z;
    }

    if (config.metadata?.mergeEdges) {
      await this.mergeEdges();
    }
  }

  async createDungeon() {
    const config = this.config.dungeon;
    if (!config) {
      return;
    }
    this.game.loadingScreen.setTask('Creating Dungeon');
    await this.dungeonGenerator.generate(config);
  }

  async createCollectibles() {
    const config = this.config.collectible;
    if (!config) {
      return;
    }
    this.game.loadingScreen.setTask('Creating Collectibles');
    await this.collectibleGenerator.generate(config);
  }

  async createSprites(): Promise<void> {
    if (this.config.sprites?.length > 0) {
      this.game.loadingScreen.setTask('Creating Sprites');

      let spriteManagers: Map<string, SpriteManager> = new Map<string, SpriteManager>();

      for (const spriteConfig of this.config.sprites) {
        if (spriteConfig.assetName) {
          spriteConfig.asset = this.game.assetFactory.getAsset(spriteConfig.assetName);
        }
        const task: ImageAssetTask = spriteConfig.asset.getTask();

        const positions = this.positionFactory.randomPositions(spriteConfig.capacity, this.getBoundingBox(), true, undefined, (p) => {
          if (p.y <= spriteConfig.yRange.min || p.y >= spriteConfig.yRange.max) return false;
          if (spriteConfig.deadZone) {
            const distFromOrigin = Math.sqrt(p.x * p.x + p.z * p.z);
            if (distFromOrigin < spriteConfig.deadZone.min || distFromOrigin > spriteConfig.deadZone.max) return false;
          }
          return true;
        });

        let spriteManager: SpriteManager;
        if (spriteConfig.atlas) {
          if (!spriteManagers.has(spriteConfig.assetName)) {
            spriteManagers.set(
              spriteConfig.assetName,
              new SpritePackedManager(spriteConfig.name + '_manager', task.image.currentSrc, 10000, this.game.scene)
            );
          }
          spriteManager = spriteManagers.get(spriteConfig.assetName);
        } else {
          if (!spriteManagers.has(spriteConfig.asset.name)) {
            spriteManagers.set(
              spriteConfig.asset.name,
              new SpriteManager(
                spriteConfig.name + '_manager',
                task.image.currentSrc,
                spriteConfig.capacity,
                { width: spriteConfig.cellWidth, height: spriteConfig.cellHeight },
                this.game.scene
              )
            );
          }
          spriteManager = spriteManagers.get(spriteConfig.asset.name);
        }

        for (const position of positions) {
          const sp = new Sprite(spriteConfig.name + '_' + positions.indexOf(position), spriteManager);
          if (spriteConfig.atlas) {
            sp.cellRef = spriteConfig.image;
          }
          if (spriteConfig.color) {
            sp.color = spriteConfig.color;
          }

          const scale = this.floatInRange(spriteConfig.scale.min, spriteConfig.scale.max);
          sp.size = scale;
          sp.position = position.add(new Vector3(0, sp.size / 2 + (spriteConfig.offsetY || 0), 0));
        }
      }
    }
  }

  async createEnemies(): Promise<void> {
    const config = this.config.enemy;
    if (!config || !config.distributions || config.distributions.length === 0) {
      return;
    }
    this.game.loadingScreen.setTask('Creating Enemies');
    await this.enemyGenerator.generate(config);
  }

  async createObstacles(): Promise<void> {
    const config = this.config.obstacle;
    if (!config || !config.distributions || config.distributions.length === 0) {
      return;
    }
    this.game.loadingScreen.setTask('Creating Obstacles');
    await this.obstacleGenerator.generate(config);
  }

  async createGround(): Promise<GroundMesh> {
    if (!this.config.ground) {
      return undefined;
    }
    this.game.loadingScreen.setTask('Creating Ground');
    const mesh = this.config.ground.buildMesh(this);
    MeshFactory.configureMesh(mesh, this.config.ground, this);
    mesh.isPickable = true;
    return mesh as GroundMesh;
  }

  async createHeightMapGround(): Promise<GroundMesh> {
    if (!this.config.heightMap) {
      return undefined;
    }
    this.game.loadingScreen.setTask('Creating Heightmap');

    // Pass pathPoints for heightmap path integration (higher & lower points) or path projection
    if (this.config.heightMap.pathWidth > 0 || this.config.heightMap.pathProjection) {
      this.config.heightMap.pathPoints = this.pathPoints;
    }

    const mesh = await this.config.heightMap.buildMeshAsync(this);

    if (this.config.heightMap.metadata?.centerOnPath) {
      const centerPathPoint = this.getCenterPathPoint();
      mesh.position.x = centerPathPoint.x;
      mesh.position.z = centerPathPoint.z;
    }
    mesh.isPickable = true;

    return mesh as GroundMesh;
  }

  async createGoal() {
    this.game.loadingScreen.setTask('Creating Goal');

    const position = this.getMaxPathPosition(this.pathPoints.length - this.config.goalEndOffset);

    // Find goal asset object
    const goalAssetObj = this.assetObjects.find((a) => a.type === 'goal');

    const config = AssetObjectConfig.builder()
      .object(goalAssetObj)
      .scale(goalAssetObj.scale)
      .position(position)
      .physicsShapeType(undefined)
      .build();

    const mesh = config.buildMesh(this);
    MeshFactory.configureMesh(mesh, config, this);

    const orientationPoint = this.pathPoints[this.pathPoints.length - this.config.goalEndOffset - 1];

    // orient y axis to face orientationPoint
    const targetPoint = new Vector3(orientationPoint.x, mesh.position.y, orientationPoint.z);
    mesh.lookAt(targetPoint);

    mesh.position.y += mesh.getBoundingInfo().boundingBox.extendSizeWorld.y * goalAssetObj.scale - 0.1;

    // Store reference for distance checking in update()
    this.goalMesh = mesh;
  }

  createPostProcesses() {
    if (this.config.postProcess) {
      this.game.loadingScreen.setTask('Creating Post Processes');

      if (this.config.postProcess.fxaa) {
        const fxaa = new FxaaPostProcess('fxaa', 1.0, this.scene.activeCamera);
      }
      if (this.config.postProcess.motionBlur) {
        const mb = new MotionBlurPostProcess('motionBlur', this.scene, 1.0, this.scene.activeCamera);
        mb.motionStrength = this.config.postProcess.motionBlur;
      }
      if (this.config.postProcess.blur) {
        const postProcess = new BlurPostProcess(
          'blur',
          new Vector2(this.config.postProcess.blur.x, this.config.postProcess.blur.y),
          this.config.postProcess.blur.kernel,
          this.config.postProcess.blur.strength,
          this.scene.activeCamera
        );
      }
      if (this.config.postProcess.highlights) {
        const highlights = new HighlightsPostProcess('highlights', 1.0, this.scene.activeCamera);
      }
      if (this.config.postProcess.sepia) {
        const sepia = new ConvolutionPostProcess('sepia', ConvolutionPostProcess.EmbossKernel, 1.0, this.scene.activeCamera);
      }
      if (this.config.postProcess.fireflies) {
        this.particles.createFireflyParticleSystem(this.config.postProcess.fireflies);
      }
      if (this.config.postProcess.rain) {
        this.particles.createRainParticleSystem(this.config.postProcess.rain);
      }
      if (this.config.postProcess.heavyRain) {
        this.particles.createHeavyRainParticleSystem(this.config.postProcess.heavyRain);
      }
      if (this.config.postProcess.advancedRain) {
        new RainBuilder(this.scene).create({
          rainHeight: 40,
          rowSize: 200,
          rippleRowSize: 200,
          density: 0.25,
          speedFactor: 0.25,
          center: this.player.model.position,
          ground: this.ground,
        });
      }
      if (this.config.postProcess.groundFog) {
        this.particles.createGroundFogParticleSystem(this.config.postProcess.groundFog);
      }
      if (this.config.postProcess.snow) {
        this.particles.createSnowParticleSystem(this.config.postProcess.snow);
      }
      if (this.config.postProcess.birds) {
        this.particles.createBirdsParticleSystem(this.config.postProcess.birds);
      }
    }
  }

  finishLevel() {
    if (this.finished) {
      return;
    }
    this.finished = true;

    // Disable input
    this.inputManager.disabled = true;

    // Stop timer
    clearInterval(this.timeLimitHandle);

    // Spectacular fireworks celebration!
    this.particles.createLevelCompleteFireworks(this.player.model.position.clone(), 4);

    this.game.sound.play('finish');

    // Dance
    this.player.playSamba();

    setTimeout(() => {
      // Stop portal particles
      if (this.goalPortalParticles) {
        this.goalPortalParticles.stop();
        this.goalPortalParticles.dispose();
      }
      const levelResult: LevelResult = {
        key: this.key,
        name: this.config.name,
        collectibles: this.player.collectibles,
        enemies: this.player.enemies,
        totalCollectibles: this.collectibles,
        totalEnemies: this.enemies,
        remainingTime: this.timeLimit,
        score: this.player.score,
        coins: this.player.coins,
        lives: this.player.lives,
        attempts: this.player.attempts,
      };
      this.game.routeTo(GameState.SUCCESS, levelResult);
    }, 4000);
  }

  async build(): Promise<void> {
    this.game.loadingScreen.setTask('Gathering Assets');
    let assets: Asset[] = this.assets;

    if (this.config.assets?.length > 0) {
      assets = assets.concat(this.config.assets);
    }

    if (this.config.atlasses) {
      for (const atlas of this.config.atlasses) {
        assets = assets.concat(atlas.assets);
      }
    }
    if (this.config.sprites) {
      for (const sprite of this.config.sprites) {
        if (sprite.asset) {
          assets = assets.concat(sprite.asset);
        }
      }
    }
    if (this.config.platform?.platformAsset) {
      assets = assets.concat(this.config.platform.platformAsset.assets);
    }

    if (this.config.platform?.movingPlatformAsset) {
      assets = assets.concat(this.config.platform.movingPlatformAsset.assets);
    }

    if (this.config.platform?.crumblingPlatformAsset) {
      assets = assets.concat(this.config.platform.crumblingPlatformAsset.assets);
    }

    if (this.config.platform?.startGroundAsset) {
      assets = assets.concat(this.config.platform.startGroundAsset.assets);
    }
    if (this.config.platform?.destinationGroundAsset) {
      assets = assets.concat(this.config.platform.destinationGroundAsset.assets);
    }

    if (this.config.decoration?.assets) {
      assets = assets.concat(this.config.decoration.assets);
    }

    if (this.config.enemy?.assets) {
      assets = assets.concat(this.config.enemy.assets);
    }

    if (this.config.obstacle?.assets) {
      assets = assets.concat(this.config.obstacle.assets);
    }

    if (assets.length > 0) {
      await this.game.assetFactory.load(assets);
    }

    if (this.config.atlasses) {
      for (const atlas of this.config.atlasses) {
        await atlas.create(this.scene);
      }
    }
    await this.createMaterials();
    this.createLight();
    this.createFog();
    await this.createSkyBox();

    await this.createDungeon();

    if (this.config.ground) {
      this.ground = await this.createGround();
    } else if (this.config.heightMap) {
      this.ground = await this.createHeightMapGround();
    }

    if (this.ground) {
      // Merge ground flag into existing metadata (e.g. projectileCollision from heightmap)
      this.ground.metadata = Object.assign(this.ground.metadata || {}, {
        ground: true,
        projectileCollision: true,
      });
    }

    if (this.config.baseScene) {
      this.baseSceneMeshes = await this.createBaseSceneMeshes();
    }

    // if (this.ground) {
    //   this.ground.checkCollisions = true;
    //   this.collisionMeshes.push(this.ground);
    // }

    await this.createCeiling();

    this.water = await this.createWater();

    if (this.config.way) {
      await this.createWay();
    }

    if (this.config.platform) {
      await this.createPlatforms();
    }

    this.checkpointGenerator.generate(); // Synchronous generation

    if (this.config.decoration) {
      await this.createDecoration();
    }

    if (this.config.collectible) {
      await this.createCollectibles();
    }

    if (this.config.obstacle) {
      await this.createObstacles();
    }

    if (this.config.enemy) {
      await this.createEnemies();
    }

    if (this.config.sprites) {
      await this.createSprites();
    }

    if (this.config.pathLights) {
      await this.createPathLights();
    }

    this.player = new Player(this);

    await this.player.init();

    // Create path boundary walls if enabled
    this.createPathBoundaries();

    await this.createGoal();

    if (this.config.grass) {
      await this.createGrass();
    }

    this.createPostProcesses();

    this.hud = new Hud(this);

    await Gui.createBackButton(this.game, GameState.SELECTION);
    await Gui.createPauseButton(this.game);

    this.startTime = Date.now();
    this.startTimer();

    //this.game.sound.textToSpeech(this.config.name);

    setTimeout(() => {
      this.hud.showMessage(this.config.name, 2000, true);
      this.game.sound.play('level-start');
    }, 1000);

    this.initialized = true;
  }

  update(deltaTime: number) {
    if (!this.initialized) {
      return;
    }

    // Apply time scale for slow motion effects
    const scaledDeltaTime = deltaTime * this.timeScale;

    this.inputManager.update();

    if (this.cameraController) {
      this.cameraController.update(scaledDeltaTime);
    }
    if (this.player && this.player.characterController) {
      this.player.update(scaledDeltaTime);
    }

    // Update enemies, obstacles & collectibles (spawning/despawning and behavior)
    this.enemyGenerator.update(scaledDeltaTime);
    this.obstacleGenerator.update(scaledDeltaTime);
    this.collectibleGenerator.update(scaledDeltaTime);
    this.checkpointGenerator.update();

    if (this.config.grass) {
      this.grassGenerator.update(scaledDeltaTime);
    }

    if (this.config.light.shadowCasterDistance) {
      // Distance-based shadow caster filtering for performance
      this.updateShadowCasters();
    }

    // Goal proximity check - activate portal effect when player is within goal range
    this.updateGoalProximity();

    // Update path boundary walls position if enabled
    if (this.pathBoundaryWalls) {
      this.updatePathBoundaries();
    }
  }

  /**
   * Check if player is close to goal and handle portal activation
   * Only runs once per second for performance
   */
  private updateGoalProximity(): void {
    if (!this.goalMesh || !this.player || this.finished || this.game.config.sceneViewer) {
      return;
    }

    // Only check once per second before activation for performance
    if (!this.goalActivated) {
      const now = Date.now();
      if (now - this.lastGoalProximityCheck < 1000) {
        return;
      }
      this.lastGoalProximityCheck = now;
    }

    // const distance = Vector3.Distance(this.player.model.position, this.goalMesh.position);
    const distance = this.getCameraDistance(this.goalMesh.position);
    const yDistance = Math.abs(this.goalMesh.position.y - this.scene.activeCamera.position.y);

    const frustumPlanes = Frustum.GetPlanes(this.scene.activeCamera.getTransformationMatrix());
    const goalInCamera = this.goalMesh.isInFrustum(frustumPlanes);

    if (distance <= 20 && yDistance <= 10 && goalInCamera && this.player.grounded && !this.goalActivated) {
      // Player entered activation zone - start portal effect
      this.goalActivated = true;
      this.goalActivationTime = Date.now();

      this.highlights.addMesh(this.goalMesh as Mesh, Color3.Yellow());

      this.game.sound.play('goal');

      // Show message
      this.hud.showMessage(this.config.name + ' completed!', 2000, true);

      // Create glowing portal effect inside the ring
      this.goalPortalParticles = this.particles.createGoalPortalEffect(this.goalMesh.position);
    }

    if (this.goalActivated && !this.finished) {
      // Check if x seconds have passed since activation
      const elapsedTime = Date.now() - this.goalActivationTime;
      if (elapsedTime >= 500) {
        this.finishLevel();
      }
    }
  }

  /**
   * Register a mesh as a shadow caster. Uses addShadowCaster on the generator
   * and tracks it for distance-based filtering if enabled.
   * @param mesh The mesh to add as shadow caster
   * @param excludeFromDistanceFilter If true, always casts shadows regardless of distance (e.g. thin instances)
   */
  addShadowCaster(mesh: AbstractMesh, excludeFromDistanceFilter: boolean = false): void {
    this.shadowGenerator.addShadowCaster(mesh);
    if (this.config.light.shadowCasterDistance > 0) {
      if (excludeFromDistanceFilter) {
        // Mark mesh as always-on caster
        mesh.metadata = { ...mesh.metadata, alwaysCastShadow: true };
      }
      this.allShadowCasters.push(mesh);
    }
  }

  /**
   * Update shadow casters based on distance from player.
   * Only meshes within shadowCasterDistance will cast shadows.
   * Meshes marked with alwaysCastShadow always cast shadows (e.g. thin instances).
   */
  private updateShadowCasters(): void {
    if (!this.player) {
      return; // Distance filtering disabled
    }

    const maxDist = this.config.light.shadowCasterDistance;
    const playerPos = this.player.model.position;
    const shadowMap = this.shadowGenerator.getShadowMap();
    if (!shadowMap) return;

    // Filter shadow casters by distance to player
    // Remove disposed meshes while we're at it
    const activeCasters: AbstractMesh[] = [];
    for (const mesh of this.allShadowCasters) {
      if (mesh.isDisposed()) continue;

      // Always include meshes marked as always cast shadow (thin instances)
      if (mesh.metadata?.alwaysCastShadow) {
        activeCasters.push(mesh);
        continue;
      }

      const dist = Vector3.Distance(mesh.position, playerPos);
      if (dist <= maxDist) {
        activeCasters.push(mesh);
      }
    }

    // Update render list with only nearby casters
    shadowMap.renderList = activeCasters;
  }

  /**
   * Sets time scale
   * @param scale Time scale (0.1 = 10x slower, 0.5 = 2x slower, 1 = normal)
   */
  setTimeScale(scale: number) {
    this.timeScale = scale;
    // Also slow down animations
    this.scene.animationGroups.forEach((group) => {
      group.speedRatio = scale;
    });
  }

  /**
   * Reset time scale to normal speed
   */
  resetTimeScale() {
    this.setTimeScale(1);
  }

  getCenterPathPoint(): Vector3 {
    return this.pathPoints[Math.floor(this.pathPoints.length / 2)];
  }

  getMaxHeightAtCoordinates(x: number, z: number): number | undefined {
    if (this.ceiling) {
      return this.ceiling.getHeightAtCoordinates(x, z);
    }
    return undefined;
  }

  getHeightAtCoordinates(x: number, z: number): number {
    if (this.baseSceneMeshes?.length > 0) {
      return this.raycastHeightAtCoordinates(x, z);
    }

    if (this.ground) {
      try {
        return this.ground.getHeightAtCoordinates(x, z);
      } catch (error) {
        return this.raycastHeightAtCoordinates(x, z);
      }
    }

    return 0;
  }

  raycastHeightAtCoordinates(x: number, z: number): number {
    const ray = new Ray(new Vector3(x, 1000, z), new Vector3(0, -1, 0));
    const hit = this.scene.pickWithRay(ray, (mesh) => mesh.metadata?.ground);
    if (hit && hit.hit) {
      return hit.pickedPoint.y;
    }
    return 0;
  }

  /**
   * Get ground face information (position and normal) at given coordinates
   * Uses raycast to retrieve face normal for decoration alignment
   * @param x X coordinate
   * @param z Z coordinate
   * @returns Object with position and normal vector, or null if no ground found
   */
  getGroundFaceAtCoordinates(x: number, z: number): { position: Vector3; normal: Vector3 } | null {
    if (!this.ground) {
      return null;
    }

    // Create downward raycast from above
    const rayOrigin = new Vector3(x, 1000, z);
    const rayDirection = new Vector3(0, -1, 0);
    const ray = new Ray(rayOrigin, rayDirection, 2000);

    // Perform raycast against ground
    const pickingInfo = this.scene.pickWithRay(ray, (mesh) => mesh === this.ground);

    if (pickingInfo && pickingInfo.hit && pickingInfo.pickedPoint) {
      // Get face normal (worldSpace = true, useWorldCoordinates = true)
      const normal = pickingInfo.getNormal(true, true);

      if (normal) {
        return {
          position: pickingInfo.pickedPoint.clone(),
          normal: normal,
        };
      }
    }

    return null;
  }

  getBoundingBoxAroundPath(spaceAround: number): BoundingBox {
    if (!this.pathPoints || this.pathPoints.length === 0) {
      return new BoundingBox(Vector3.Zero(), Vector3.Zero());
    }

    // Find min/max coordinates across all path points
    let minX = Number.MAX_VALUE;
    let minY = Number.MAX_VALUE;
    let minZ = Number.MAX_VALUE;
    let maxX = Number.MIN_VALUE;
    let maxY = Number.MIN_VALUE;
    let maxZ = Number.MIN_VALUE;

    for (const point of this.pathPoints) {
      minX = Math.min(minX, point.x);
      minY = Math.min(minY, point.y);
      minZ = Math.min(minZ, point.z);
      maxX = Math.max(maxX, point.x);
      maxY = Math.max(maxY, point.y);
      maxZ = Math.max(maxZ, point.z);
    }

    // Apply spaceAround padding
    const min = new Vector3(minX - spaceAround, minY - spaceAround, minZ - spaceAround);
    const max = new Vector3(maxX + spaceAround, maxY + spaceAround, maxZ + spaceAround);

    return new BoundingBox(min, max);
  }

  getBoundingBox(): BoundingBox {
    let boundingBox: BoundingBox;
    if (!this.ground) {
      boundingBox = this.getBoundingBoxAroundPath(this.config.path.spaceAround);
    } else {
      boundingBox = this.ground.getBoundingInfo().boundingBox;
    }

    // scale the bounding box to the height above ground
    return new BoundingBox(boundingBox.minimum, boundingBox.maximum.add(new Vector3(0, this.config.heightAboveGround, 0)));
  }

  integerInRange(min: number, max: number): number {
    return Math.round(this.rng.uFloat32() * (max - min) + min);
  }

  floatInRange(min: number, max: number): number {
    return this.rng.uFloat32() * (max - min) + min;
  }

  randomEnum<T>(anEnum: T): T[keyof T] {
    const keys = Object.keys(anEnum).filter((k) => isNaN(Number(k)));
    const values = keys.map((k) => (anEnum as any)[k]);
    return this.randomArrayElement(values) as T[keyof T];
  }

  /**
   * Gets a random direction vector
   */
  randomDirection(): Vector3 {
    const directions = [
      new Vector3(1, 0, 0), // right
      new Vector3(-1, 0, 0), // left
      new Vector3(0, 0, 1), // forward
      new Vector3(0, 0, -1), // backward
    ];

    return this.randomArrayElement(directions);
  }

  randomArrayElementExhausting<T>(array: T[]): T {
    if (array.length === 0) {
      return undefined;
    }
    if (array.length === 1) {
      return array.splice(0, 1)[0];
    }
    const nextIndex = this.integerInRange(0, array.length - 1);
    const nextValue = array[nextIndex];
    array.splice(nextIndex, 1);
    return nextValue;
  }

  randomArrayElements<T>(array: T[], count: number): T[] {
    if (array.length === 0) {
      return undefined;
    }
    if (array.length === 1) {
      return array;
    }
    const copy = array.concat([]);
    const result: T[] = [];
    for (let i = 0; i < count; i++) {
      result.push(this.randomArrayElementExhausting(copy));
    }
    return result;
  }

  randomArrayElement<T>(array: T[]): T {
    if (array.length === 0) {
      return undefined;
    }
    if (array.length === 1) {
      return array[0];
    }
    const nextIndex = this.integerInRange(0, array.length - 1);
    return array[nextIndex];
  }

  shuffleArray(array: any[]) {
    let currentIndex = array.length;

    // While there remain elements to shuffle...
    while (currentIndex != 0) {
      // Pick a remaining element...
      let randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
  }

  getPlatformByPathIndex(pathIndex: number): Platform {
    return this.platforms.find((platform) => platform.pathIndex === pathIndex);
  }

  getWayMeshByPathIndex(pathIndex: number): WayMesh {
    return this.wayMeshes.find((wayMesh) => wayMesh.pathIndexes.includes(pathIndex));
  }

  getRoomByPathIndex(pathIndex: number): DungeonRoom {
    if (!this.config.dungeon) {
      return undefined;
    }
    return this.dungeonGenerator.getRoomByPathIndex(pathIndex);
  }

  getPathInfo(pathIndex: number): PathInfo {
    const pathPoint = this.pathPoints[pathIndex];
    const nextPoint = this.pathPoints[Math.min(pathIndex + 1, this.pathPoints.length - 1)];
    const prevPoint = this.pathPoints[Math.max(pathIndex - 1, 0)];
    const tangent = nextPoint.subtract(prevPoint).normalize();
    const lateral = new Vector3(-tangent.z, 0, tangent.x);
    const yRotation = Math.atan2(lateral.x, lateral.z);
    return {
      pathIndex,
      pathPoint,
      nextPoint,
      prevPoint,
      tangent,
      lateral,
      yRotation,
    };
  }

  /**
   * Create path boundary walls that follow the player and prevent leaving the path
   */
  createPathBoundaries() {
    if (!this.config.path.pathBoundary) {
      return;
    }

    const wallHeight = 100;
    const wallThickness = 0.5;
    const wallLength = this.config.path.pathBoundary * 10;

    // Create left wall
    // width = perpendicular to path (thin), height = vertical, depth = along path (long)
    const leftWall = MeshBuilder.CreateBox('pathBoundaryLeft', { width: wallThickness, height: wallHeight, depth: wallLength }, this.scene);

    // Create right wall
    const rightWall = MeshBuilder.CreateBox(
      'pathBoundaryRight',
      { width: wallThickness, height: wallHeight, depth: wallLength },
      this.scene
    );

    if (this.game.config.debug && this.game.config.debugPathBoundaries) {
      rightWall.visibility = 0.3;
      leftWall.visibility = 0.3;
    } else {
      rightWall.isVisible = false;
      leftWall.isVisible = false;
    }

    rightWall.isPickable = false;
    leftWall.isPickable = false;
    rightWall.checkCollisions = true;
    leftWall.checkCollisions = true;

    // Configure physics for both walls - use ANIMATED so they can move
    const meshConfigLeft = ExistingMeshConfig.builder()
      .name('pathBoundaryLeft')
      .physicsShapeType(PhysicsShapeType.BOX)
      .physicsMotionType(PhysicsMotionType.ANIMATED)
      .mesh(leftWall)
      .build();
    MeshFactory.configureMesh(leftWall, meshConfigLeft, this);

    const meshConfigRight = ExistingMeshConfig.builder()
      .name('pathBoundaryRight')
      .physicsShapeType(PhysicsShapeType.BOX)
      .physicsMotionType(PhysicsMotionType.ANIMATED)
      .mesh(rightWall)
      .build();
    MeshFactory.configureMesh(rightWall, meshConfigRight, this);

    this.pathBoundaryWalls = { left: leftWall, right: rightWall };

    // Initial positioning
    this.updatePathBoundaries();
  }

  /**
   * Update path boundary walls position based on player's current path index
   */
  updatePathBoundaries() {
    if (!this.pathBoundaryWalls || !this.player || !this.player.currentPathIndex) {
      return;
    }

    const pathInfo = this.getPathInfo(this.player.currentPathIndex);
    const offset = this.config.path.pathBoundary;

    // Calculate rotation to align with path direction
    const yRotation = Math.atan2(pathInfo.tangent.x, pathInfo.tangent.z);

    // Position left wall
    const leftPosition = pathInfo.pathPoint.add(pathInfo.lateral.scale(-offset));
    leftPosition.y += 13;

    // Position right wall
    const rightPosition = pathInfo.pathPoint.add(pathInfo.lateral.scale(offset));
    rightPosition.y += 13;

    // Update mesh positions
    this.pathBoundaryWalls.left.position.copyFrom(leftPosition);
    this.pathBoundaryWalls.right.position.copyFrom(rightPosition);

    // Update rotations using quaternions (required for physics bodies)
    const rotationQuat = Quaternion.FromEulerAngles(0, yRotation, 0);

    this.pathBoundaryWalls.left.rotationQuaternion = rotationQuat.clone();
    this.pathBoundaryWalls.right.rotationQuaternion = rotationQuat.clone();

    // Update physics body transforms
    if (this.pathBoundaryWalls.left.physicsBody) {
      this.pathBoundaryWalls.left.physicsBody.setTargetTransform(leftPosition, rotationQuat);
    }
    if (this.pathBoundaryWalls.right.physicsBody) {
      this.pathBoundaryWalls.right.physicsBody.setTargetTransform(rightPosition, rotationQuat);
    }
  }

  getPathRotation(tangent: Vector3, yRotationOffset?: number): Vector3 {
    // Calculate Y rotation (yaw) to align with path direction in XZ plane
    const offset = yRotationOffset || 0;
    const yRotation = Math.atan2(tangent.x, tangent.z) + offset;

    // Calculate X rotation (pitch) to follow the slope of the path
    // atan2(y, horizontal_length) gives the pitch angle
    const horizontalLength = Math.sqrt(tangent.x * tangent.x + tangent.z * tangent.z);
    let xRotation = -Math.atan2(tangent.y, horizontalLength);

    // If rotationOffset flips the mesh direction (around 180°), invert pitch
    // to keep the slope alignment correct
    const normalizedOffset = Math.abs(offset % (Math.PI * 2));
    if (normalizedOffset > Math.PI / 2 && normalizedOffset < (3 * Math.PI) / 2) {
      xRotation = -xRotation;
    }

    return new Vector3(xRotation, yRotation, 0);
  }

  getMaxPathPosition(pathIndex: number): Vector3 {
    let position = this.pathPoints[pathIndex].clone();
    if (this.config.platform) {
      const platform = this.getPlatformByPathIndex(pathIndex);
      if (platform) {
        // get max y position of the platform
        const max = platform.mesh.getBoundingInfo().boundingBox.maximumWorld;
        position.y = max.y;
      }
    }
    return position;
  }

  /**
   * Get distance from a point to camera
   */
  getCameraDistance(position: Vector3): number {
    return Vector3.Distance(position, this.scene.activeCamera.position);
  }

  getAtlasByName(name: string): TextureAtlas {
    return this.config.atlasses.find((atlas) => atlas.name === name);
  }

  getTexture(materialIndexOrName: string | number): Texture {
    const material = this.getMaterial(materialIndexOrName);

    if (Level.usePbrMaterial) {
      return (material as PBRMaterial).albedoTexture as Texture;
    }

    return (material as StandardMaterial).diffuseTexture as Texture;
  }

  getMaterial(materialIndexOrName: string | number): Material {
    if (typeof materialIndexOrName === 'number') {
      return this.materials[materialIndexOrName];
    }
    const index = this.getMaterialIndexByName(materialIndexOrName);
    if (index === -1) {
      throw new Error('Material not found: ' + materialIndexOrName);
    }
    return this.materials[index];
  }

  getMaterialIndexByName(name: string): number {
    for (let i = 0; i < this.materials.length; i++) {
      if (this.materials[i].name === name) {
        return i;
      }
    }
    return -1;
  }

  private startTimer() {
    if (this.config.timeLimit !== undefined) {
      this.hurryUpShown = false;
      this.timeLimitHandle = setInterval(() => {
        if (this.game.paused || this.game.config.sceneViewer) {
          return;
        }
        this.timeLimit--;

        // "Hurry Up" warning at 1/3 time remaining (only once)
        if (!this.hurryUpShown && this.timeLimit < this.config.timeLimit / 3) {
          const message = 'Hurry Up!!!';
          this.hud.showMessage(message, 1000, true);
          this.game.sound.speak(message);
          this.game.sound.play('clock-ticking');
          this.hurryUpShown = true;
        }

        if (this.timeLimit === 10) {
          this.game.sound.play('clock-ticking');
        }
        // Audible countdown from 10 seconds
        if (this.timeLimit <= 10 && this.timeLimit > 0) {
          this.game.sound.speak(this.timeLimit.toString());
        }

        if (this.timeLimit === 0) {
          this.player.collide(undefined, 'Time is up!!!');
          this.timeLimit = this.config.timeLimit;
          this.hurryUpShown = false; // Reset for next attempt
        }
      }, 1000);
    }
  }

  private async mergeEdges() {
    // we want to merge the edges of the ground and ceiling, to create a closed environment
    // both of them are ground meshes so we can push and pull the matrices to merge the edges
    if (!this.ground || !this.ceiling) {
      return;
    }

    // Ensure world matrices are up to date
    this.ground.computeWorldMatrix(true);
    this.ceiling.computeWorldMatrix(true);

    const groundMatrix = this.ground.getWorldMatrix();
    const ceilingMatrix = this.ceiling.getWorldMatrix();
    const groundInvMatrix = groundMatrix.clone().invert();
    const ceilingInvMatrix = ceilingMatrix.clone().invert();

    // Helper to find border vertices (min/max X/Z)
    const getBorderVertices = (mesh: Mesh) => {
      const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
      const indices: number[] = [];
      if (!positions) return { positions, indices };

      // Find extents
      let minX = Infinity,
        maxX = -Infinity,
        minZ = Infinity,
        maxZ = -Infinity;
      for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i];
        const z = positions[i + 2];
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (z < minZ) minZ = z;
        if (z > maxZ) maxZ = z;
      }

      const epsilon = 0.1;
      for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i];
        const z = positions[i + 2];
        if (Math.abs(x - minX) < epsilon || Math.abs(x - maxX) < epsilon || Math.abs(z - minZ) < epsilon || Math.abs(z - maxZ) < epsilon) {
          indices.push(i / 3);
        }
      }
      return { positions, indices };
    };

    const groundData = getBorderVertices(this.ground);
    const ceilingData = getBorderVertices(this.ceiling);

    if (!groundData.indices.length || !ceilingData.indices.length) return;

    // Cache ceiling border world positions for performance
    const ceilingBorderWorld: { index: number; pos: Vector3 }[] = [];
    const tempVec = Vector3.Zero();
    for (const cIndex of ceilingData.indices) {
      const idx = cIndex * 3;
      tempVec.set(ceilingData.positions[idx], ceilingData.positions[idx + 1], ceilingData.positions[idx + 2]);
      ceilingBorderWorld.push({
        index: cIndex,
        pos: Vector3.TransformCoordinates(tempVec, ceilingMatrix),
      });
    }

    let modified = false;
    const maxDistSq = 50 * 50; // Threshold for matching vertices

    // Find matches and pull vertices together
    // Note: We modify groundData.positions and ceilingData.positions in place
    // but use valid world coordinates derived from original positions
    for (const gIndex of groundData.indices) {
      const idx = gIndex * 3;
      tempVec.set(groundData.positions[idx], groundData.positions[idx + 1], groundData.positions[idx + 2]);
      const groundWorldPos = Vector3.TransformCoordinates(tempVec, groundMatrix);

      let closestCeiling = null;
      let minDistSq = maxDistSq;

      // Find closest ceiling border vertex in XZ
      for (const cw of ceilingBorderWorld) {
        const dx = groundWorldPos.x - cw.pos.x;
        const dz = groundWorldPos.z - cw.pos.z;
        const distSq = dx * dx + dz * dz;

        if (distSq < minDistSq) {
          minDistSq = distSq;
          closestCeiling = cw;
        }
      }

      if (closestCeiling) {
        // Average Y
        const avgY = (groundWorldPos.y + closestCeiling.pos.y) / 2;

        // Apply to Ground
        const newGroundWorld = new Vector3(groundWorldPos.x, avgY, groundWorldPos.z);
        const newGroundLocal = Vector3.TransformCoordinates(newGroundWorld, groundInvMatrix);
        groundData.positions[idx] = newGroundLocal.x;
        groundData.positions[idx + 1] = newGroundLocal.y;
        groundData.positions[idx + 2] = newGroundLocal.z;

        // Apply to Ceiling
        // Note: For ceiling, we also transform based on its original XZ world pos + new Y
        const newCeilingWorld = new Vector3(closestCeiling.pos.x, avgY, closestCeiling.pos.z);
        const newCeilingLocal = Vector3.TransformCoordinates(newCeilingWorld, ceilingInvMatrix);
        const cIdx = closestCeiling.index * 3;
        ceilingData.positions[cIdx] = newCeilingLocal.x;
        ceilingData.positions[cIdx + 1] = newCeilingLocal.y;
        ceilingData.positions[cIdx + 2] = newCeilingLocal.z;

        modified = true;
      }
    }

    if (modified) {
      this.ground.updateVerticesData(VertexBuffer.PositionKind, groundData.positions);
      this.ceiling.updateVerticesData(VertexBuffer.PositionKind, ceilingData.positions);

      // Recalculate normals to ensure lighting looks correct at the seam
      const groundNormals: number[] = [];
      VertexData.ComputeNormals(groundData.positions, this.ground.getIndices(), groundNormals);
      this.ground.updateVerticesData(VertexBuffer.NormalKind, groundNormals);

      const ceilingNormals: number[] = [];
      VertexData.ComputeNormals(ceilingData.positions, this.ceiling.getIndices(), ceilingNormals);
      this.ceiling.updateVerticesData(VertexBuffer.NormalKind, ceilingNormals);

      this.ground.refreshBoundingInfo();
      this.ceiling.refreshBoundingInfo();
    }
  }

  dispose() {
    clearInterval(this.timeLimitHandle);
    this.hud.dispose();

    for (const platform of this.platforms) {
      platform.mesh.physicsBody.dispose();
      platform.mesh.dispose();
    }
    this.collectibleGenerator.dispose();
    this.decorationGenerator.dispose();
    this.enemyGenerator.dispose();
    this.obstacleGenerator.dispose();
    this.checkpointGenerator.dispose();
  }
}
